import { clamp } from 'ramda';

/** The minimum height of the scrollbar thumb as a fraction of the scrollbar's height. */
const MIN_THUMB_LENGTH = 0.05;

/**
 * The duration after scrolling or resizing for which the scrollable is considered to have been
 * active recently, extended whenever the pointer moves.
 */
const RECENTLY_ACTIVE_DURATION_MS = 2000;

export class ScrollbarController {
  private direction: 'horizontal' | 'vertical';

  /**
   * See the comments in useScrollbarStyles and useScrollbarStyles for explanations of each
   * element's purpose.
   */
  private scrollable: HTMLDivElement;
  private scrollableInner: HTMLDivElement;
  private scrollbar: HTMLDivElement;
  private scrollbarInner: HTMLDivElement;
  private scrollbarThumb: HTMLDivElement;

  private dragState: { previousBodyUserSelect: string; previousPointerPosition: number } | null;
  private recentlyActiveTimer?: ReturnType<typeof setTimeout>;

  constructor(options: {
    direction: 'horizontal' | 'vertical';
    scrollable: HTMLDivElement;
    scrollableInner: HTMLDivElement;
    scrollbar: HTMLDivElement;
    scrollbarInner: HTMLDivElement;
    scrollbarThumb: HTMLDivElement;
  }) {
    this.direction = options.direction;
    this.scrollable = options.scrollable;
    this.scrollableInner = options.scrollableInner;
    this.scrollbar = options.scrollbar;
    this.scrollbarInner = options.scrollbarInner;
    this.scrollbarThumb = options.scrollbarThumb;
  }

  /**
   * Attach event handlers to all relevant DOM elements so that the scrollbar can function.
   * @returns {() => void} a function to disconnect the scrollbar
   */
  connect(): () => void {
    this.updateElements();

    const onScroll = this.onScroll.bind(this);
    const onWheelScrollbar = this.onWheelScrollbar.bind(this);
    const onPointerOverScrollbar = this.onPointerOverScrollbar.bind(this);
    const onPointerOutScrollbar = this.onPointerOutScrollbar.bind(this);
    const onPointerDownScrollbar = this.onPointerDownScrollbar.bind(this);
    const onPointerDownThumb = this.onPointerDownThumb.bind(this);
    const onPointerMove = this.onPointerMove.bind(this);
    const onPointerUp = this.onPointerUp.bind(this);

    this.scrollable.addEventListener('scroll', onScroll);
    this.scrollbar.addEventListener('wheel', onWheelScrollbar, { passive: false });
    this.scrollbar.addEventListener('pointerover', onPointerOverScrollbar);
    this.scrollbar.addEventListener('pointerout', onPointerOutScrollbar);
    this.scrollbarInner.addEventListener('pointerdown', onPointerDownScrollbar);
    this.scrollbarThumb.addEventListener('pointerdown', onPointerDownThumb);

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    const resizeObserver = new ResizeObserver(this.onResize.bind(this));
    resizeObserver.observe(this.scrollable);
    resizeObserver.observe(this.scrollableInner);

    return () => {
      this.scrollable.removeEventListener('scroll', onScroll);
      this.scrollbar.removeEventListener('wheel', onWheelScrollbar);
      this.scrollbar.removeEventListener('pointerover', onPointerOverScrollbar);
      this.scrollbar.removeEventListener('pointerout', onPointerOutScrollbar);
      this.scrollbarInner.removeEventListener('pointerdown', onPointerDownScrollbar);
      this.scrollbarThumb.removeEventListener('pointerdown', onPointerDownThumb);

      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      resizeObserver.disconnect();

      this.endDrag();
      this.endHover();
      this.clearRecentlyActive();
    };
  }

  /** Called when the scrollable is scrolled. */
  private onScroll() {
    this.updateElements();
    this.onActivity();
  }

  /** Called when the scrollable or its contents change in size. */
  private onResize() {
    this.updateElements();
    this.onActivity();
  }

  /**
   * Called when the user uses the native scroll wheel on the scrollbar. By default, this has no
   * effect, so we forward the scroll change to the scrollable.
   *
   * Note that the deltaX and deltaY properties on WheelEvent are known to be inconsistent across
   * platforms, so this may produce unexpected results in some cases, such as scrolling in the wrong
   * direction or by the wrong amount. On macOS, for example, attempting to pinch-to-zoom on the
   * scrollbar will instead scroll it. However, this is better than the scroll wheel having no
   * effect on the scrollbar at all.
   *
   * This method is unrelated to using the scroll wheel on the scrollable, which is handled natively
   * by the browser.
   */
  private onWheelScrollbar(event: WheelEvent) {
    event.preventDefault();
    const { deltaX, deltaY } = event;
    this.scrollable.scrollBy(deltaX, deltaY);
  }

  /** Called when the pointer begins hovering over the scrollbar. */
  private onPointerOverScrollbar() {
    this.startHover();
  }

  /** Called when the pointer ceases hovering over the scrollbar. */
  private onPointerOutScrollbar() {
    this.endHover();
  }

  /**
   * Called when the pointer begins clicking on the scrollbar. Immediately scroll to the clicked
   * location and begin dragging the scrollbar thumb.
   */
  private onPointerDownScrollbar(event: MouseEvent) {
    // Do not handle prevented events, such as if the event was also fired on the scrollbar thumb
    if (event.defaultPrevented) return;
    event.preventDefault();
    const pointerPosition = this.getPointerPosition(event);
    const { end, start } = this.measureScrollbar();
    const scrollbarHeight = end - start;
    const relativePosition = (pointerPosition - start) / scrollbarHeight;
    this.centerViewportOnRelativePosition(relativePosition);
    this.startDrag(pointerPosition);
  }

  /** Called when the pointer begins clicking on the scrollbar thumb. */
  private onPointerDownThumb(event: MouseEvent) {
    if (event.defaultPrevented) return;
    event.preventDefault();
    this.startDrag(this.getPointerPosition(event));
  }

  /**
   * Called when the pointer moves, regardless of whether the user is currently hovering over the
   * scrollbar or dragging the scrollbar thumb.
   */
  private onPointerMove(event: MouseEvent) {
    if (this.recentlyActiveTimer) this.onActivity();

    if (this.isDragging) {
      event.preventDefault();
      this.dragThumb(this.getPointerPosition(event));
    }
  }

  /**
   * Called when the pointer ceases to click, regardless of the position of the pointer or the
   * dragging state.
   */
  private onPointerUp() {
    if (this.isDragging) this.endDrag();
  }

  /**
   * Called when the scroll position changes, the size of the scrollable or its content changes, or
   * when the pointer moves before RECENTLY_ACTIVE_DURATION_MS has elapsed.
   */
  private onActivity() {
    if (this.recentlyActiveTimer) clearTimeout(this.recentlyActiveTimer);
    this.scrollbar.dataset.scrollbarRecentlyActive = 'true';
    this.recentlyActiveTimer = setTimeout(
      () => this.clearRecentlyActive(),
      RECENTLY_ACTIVE_DURATION_MS
    );
  }

  /** Clean up the effects of onActivity. */
  private clearRecentlyActive() {
    delete this.scrollbar.dataset.scrollbarRecentlyActive;
    this.recentlyActiveTimer = undefined;
  }

  /** Add a data attribute to the scrollbar indicating that the user is hovering over it. */
  private startHover() {
    this.scrollbar.dataset.scrollbarHover = 'true';
  }

  /** Clean up the effects of startHover. */
  private endHover() {
    delete this.scrollbar.dataset.scrollbarHover;
  }

  /** Begin dragging the scrollbar thumb, given the initial position of the pointer. */
  private startDrag(pointerPosition: number) {
    const previousBodyUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    this.dragState = { previousBodyUserSelect, previousPointerPosition: pointerPosition };
    this.scrollbar.dataset.scrollbarDragging = 'true';
  }

  /** Terminate a drag, cleaning up the effects of startDrag. */
  private endDrag() {
    if (!this.dragState) return;
    const { previousBodyUserSelect } = this.dragState;
    document.body.style.userSelect = previousBodyUserSelect;
    this.dragState = null;
    delete this.scrollbar.dataset.scrollbarDragging;
  }

  /** Drag the scrollbar thumb according to the new position of the pointer. */
  private dragThumb(pointerPosition: number) {
    if (!this.dragState) throw new Error('Not currently dragging');
    const { scrollOffset: previousScrollOffset } = this.getScrollableMetrics();
    const { previousPointerPosition } = this.dragState;

    // Scroll the scrollable proportionally to the change in pointer position since last time
    const deltaPointerPosition = pointerPosition - previousPointerPosition;
    this.scrollable[this.isHorizontal ? 'scrollLeft' : 'scrollTop'] +=
      deltaPointerPosition * this.getDragSpeed();

    /**
     * Only update the previous pointer position if the scroll offset has actually changed. This
     * means that if the user tries to drag the scrollbar thumb past either end of the scrollbar
     * track, dragging only resumes after the pointer returns to the position where this occurred.
     */
    const { scrollOffset } = this.getScrollableMetrics();
    const scrollOffsetChanged = scrollOffset !== previousScrollOffset;
    if (scrollOffsetChanged) this.dragState.previousPointerPosition = pointerPosition;
  }

  /** Update the scrollbar's DOM elements according to the current scroll state. */
  private updateElements() {
    const { scrollOffset, scrollSize, viewportSize } = this.getScrollableMetrics();

    const overflowing = scrollSize > viewportSize;
    this.scrollbar.dataset.scrollableOverflowing = overflowing.toString();

    const lengthKey = this.isHorizontal ? 'width' : 'height';
    const translateFn = this.isHorizontal ? 'translateX' : 'translateY';

    if (!overflowing) {
      this.scrollbarThumb.style[lengthKey] = '100%';
      this.scrollbarThumb.style.transform = `${translateFn}(0)`;
      this.scrollbarInner.ariaValueNow = '0';
      return;
    }

    /** The length of the scrollbar thumb as a fraction of the scrollbar's length. */
    const relativeThumbLength = clamp(MIN_THUMB_LENGTH, 1, viewportSize / scrollSize);

    /**
     * The offset of the scrollbar thumb as a fraction of the scrollbar thumb's length.
     *
     * The formula is derived as follows: Consider an imaginary vertical scrollbar thumb with no
     * minimum length (as if MIN_THUMB_LENGTH were 0). To increase this thumb to its desired minimum
     * length, we extend it upwards by Δl and downwards by Δl (increasing its total length by 2Δl),
     * keeping it centered on the same position as before. We also increase the length of the
     * scrollbar itself in the same way, upwards by Δl and downwards by Δl. The value of Δl is such
     * that the new length of the thumb divided by the new length of the scrollbar is exactly
     * MIN_THUMB_LENGTH. (A formula for Δl can be found using simple algebra.)
     *
     * As the user scrolls the scrollable, the offset of our original, unscaled imaginary scrollbar
     * thumb can be expressed as a fraction of that same thumb's length (since CSS percentage
     * transforms are relative to the size of the element). This fraction is equal to scrollOffset /
     * viewportSize (intuitively, the number of viewport sizes we've already scrolled through).
     * However, deriving this same fraction for the scaled scrollbar is more complex.
     *
     * As we drag our imaginary, unscaled scrollbar thumb along its track, we can also drag our
     * scaled scrollbar thumb by exactly the same number of pixels. When the unscaled thumb is at
     * the start, the scaled thumb is at the start; when the unscaled thumb is at the end, the
     * scaled thumb is at the end. Since the correct offset in pixels can be easily derived
     * in this manner, as can the length of the scaled thumb (the unscaled length + 2Δl), we can
     * with some simple algebra divide the former by the latter and derive the formula below.
     */
    const relativeThumbOffset =
      scrollOffset /
      (viewportSize +
        (relativeThumbLength * scrollSize - viewportSize) / (1 - relativeThumbLength));

    const relativeScrollOffset = scrollOffset / (scrollSize - viewportSize);

    this.scrollbarThumb.style[lengthKey] = `${relativeThumbLength * 100}%`;
    this.scrollbarThumb.style.transform = `${translateFn}(${relativeThumbOffset * 100}%)`;
    this.scrollbarInner.ariaValueNow = (relativeScrollOffset * 100).toString();
  }

  /** Get the start and end of the scrollbar track in viewport coordinates. */
  private measureScrollbar() {
    const { bottom, left, right, top } = this.scrollbarInner.getBoundingClientRect();
    return {
      end: this.isHorizontal ? right : bottom,
      start: this.isHorizontal ? left : top,
    };
  }

  /** Get the relevant measurements of the scrollable in the axis of the scrollbar. */
  private getScrollableMetrics() {
    const { clientHeight, clientWidth, scrollHeight, scrollLeft, scrollTop, scrollWidth } =
      this.scrollable;

    const scrollSize = this.isHorizontal ? scrollWidth : scrollHeight;
    const viewportSize = this.isHorizontal ? clientWidth : clientHeight;

    // WebKit can produce scrollTop and scrollLeft values that exceed the bounds of the scrollable
    const unclampedScrollOffset = this.isHorizontal ? scrollLeft : scrollTop;
    const scrollOffset = clamp(0, scrollSize - viewportSize, unclampedScrollOffset);

    return { scrollOffset, scrollSize, viewportSize };
  }

  /**
   * Get the conversion factor between pixels in the scrollbar track and pixels in the scrollable.
   * This determines how fast the content should scroll when dragging the scrollbar thumb.
   */
  private getDragSpeed() {
    const { scrollSize } = this.getScrollableMetrics();
    const { end, start } = this.measureScrollbar();
    return scrollSize / (end - start);
  }

  /**
   * Scroll the scrollable such that a given relative position (where 0 is the start and 1 is the
   * end) is as close as possible to the center of the viewport.
   */
  private centerViewportOnRelativePosition(relativePosition: number) {
    const { scrollSize, viewportSize } = this.getScrollableMetrics();
    this.scrollable[this.isHorizontal ? 'scrollLeft' : 'scrollTop'] =
      scrollSize * relativePosition - viewportSize / 2;
  }

  /** Check if the user is currently dragging the scrollbar thumb. */
  private get isDragging() {
    return !!this.dragState;
  }

  /** Get the position of the pointer in viewport coordinates in the axis of the scrollbar. */
  private getPointerPosition(event: MouseEvent) {
    return this.isHorizontal ? event.clientX : event.clientY;
  }

  /** Check if the scrollbar is horizontal (or vertical). */
  private get isHorizontal() {
    return this.direction === 'horizontal';
  }
}
